import axios from 'axios';
import api from './api';
import { getStoredSessionToken } from './sessionTokens';
import { resolveChatBaseUrl } from './runtimeConfig';

export const liveChatBaseUrl = resolveChatBaseUrl();

export const liveChatWsBaseUrl = liveChatBaseUrl
  .replace(/^https:\/\//, 'wss://')
  .replace(/^http:\/\//, 'ws://');

const liveChatApi = axios.create({
  baseURL: liveChatBaseUrl,
  headers: { 'Content-Type': 'application/json' },
});

export const centralChatTicketsEnabled = String(
  process.env.REACT_APP_CENTRAL_CHAT_TICKETS || ''
).toLowerCase() === 'true';

let cachedChatTicket = null;
let chatTicketRequest = null;

export const clearLiveChatTicket = () => {
  cachedChatTicket = null;
  chatTicketRequest = null;
};

export const getLiveChatAccessToken = async ({ force = false } = {}) => {
  const sessionToken = getStoredSessionToken();
  if (!sessionToken || !centralChatTicketsEnabled) return sessionToken;
  const nowSeconds = Math.floor(Date.now() / 1000);
  if (!force && cachedChatTicket?.sessionToken === sessionToken && cachedChatTicket.expiresAt > nowSeconds + 30) {
    return cachedChatTicket.accessToken;
  }
  if (!force && chatTicketRequest?.sessionToken === sessionToken) return chatTicketRequest.promise;
  const promise = api.post('/api/auth/chat-ticket', {}).then(response => {
    const accessToken = String(response.data?.access_token || '').trim();
    const expiresAt = Number(response.data?.expires_at || 0);
    if (!accessToken || !Number.isFinite(expiresAt) || expiresAt <= nowSeconds) {
      throw new Error('Chat ticket endpoint returned an invalid token');
    }
    if (getStoredSessionToken() !== sessionToken) {
      throw new Error('Application session changed while issuing a chat ticket');
    }
    cachedChatTicket = { sessionToken, accessToken, expiresAt };
    return accessToken;
  }).finally(() => {
    if (chatTicketRequest?.promise === promise) chatTicketRequest = null;
  });
  chatTicketRequest = { sessionToken, promise };
  return promise;
};

liveChatApi.interceptors.request.use(async (config) => {
  const token = await getLiveChatAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

liveChatApi.interceptors.response.use(
  response => response,
  async error => {
    const config = error?.config;
    if (centralChatTicketsEnabled && error?.response?.status === 401 && config && !config._chatTicketRetried) {
      config._chatTicketRetried = true;
      clearLiveChatTicket();
      const token = await getLiveChatAccessToken({ force: true });
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return liveChatApi(config);
    }
    return Promise.reject(error);
  }
);

export const createLiveChatSocketTicket = async ({ kind, conversationId = null }) => {
  if (!centralChatTicketsEnabled) return null;
  const response = await liveChatApi.post('/api/chat/ws-ticket', { kind, conversation_id: conversationId });
  const ticket = String(response.data?.ticket || '').trim();
  if (!ticket) throw new Error('Chat WebSocket ticket endpoint returned no ticket');
  return ticket;
};

export default liveChatApi;
